import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { CancellationViewResponse } from 'the-room-of-requirement/Components/Cancellation'

import { getCancellationReview, submitCancellation, verifyUpiId, getCancellationView } from '@/api/cancellation'
import { fetchCancellationOrderDetails } from '@/api/cancellationConfirm'
import type { RootState } from '@/redux/store/configureStore'
import type { CancellationRefundSnapshot } from '@/types/pages/cancellationConfirm'
import { isEmpty } from '@/utils/general/browserHelper'

interface CancellationErrorLabels {
  title?: string
  description?: string
}

export type CancellationState = {
  review: Record<string, any>
  isSubmitting: boolean
  cancellationId: string
  uiStates: {
    selectedRefundMode: string
    upiId: string
  }
  isUpiIdStatusLoading: boolean
  upiIdStatus: Record<string, any>
  cancellationErrorVariant: string | null
  cancellationErrorLabels: CancellationErrorLabels | null
  view: CancellationViewResponse
  selectedPaxIds: Record<string, string[]>
  reasonCode: string
  quotePayload: Record<string, unknown>
  orderDetails: CancellationRefundSnapshot | null
  isOrderDetailsLoading: boolean
  orderDetailsError: string | null
  apiFetchStatus: Record<string, boolean>
}

const initialState: CancellationState = {
  review: {},
  isSubmitting: false,
  cancellationId: '',
  uiStates: {
    selectedRefundMode: '',
    upiId: '',
  },
  apiFetchStatus: {
    isReviewLoading: true,
    isViewLoading: true,
    isViewFailed: false,
    isReviewFailed: false,
    isCancelConfirmLoading: false,
    isCancelConfirmFailed: false,
  },
  isUpiIdStatusLoading: false,
  upiIdStatus: {},
  cancellationErrorVariant: null,
  cancellationErrorLabels: null,
  view: {} as CancellationViewResponse,
  selectedPaxIds: {},
  reasonCode: '',
  quotePayload: {},
  orderDetails: null,
  isOrderDetailsLoading: false,
  orderDetailsError: null,
}

export const fetchCancellationReviewData = createAsyncThunk(
  'cancellation/review',
  async ({ orderId, quotePayload }: { orderId: string; quotePayload: Record<string, unknown> }) => {
    return getCancellationReview(orderId, quotePayload)
  },
)

export const fetchCancellationViewData = createAsyncThunk(
  'cancellation/view',
  async (orderId: string) => {
    const response = await getCancellationView(orderId)
    return response
  },
)

interface SubmitCancellationRejectValue {
  variant: string
  labels?: CancellationErrorLabels
}

export const submitCancellationRequest = createAsyncThunk<
  { data: any },
  { orderId: string; payload: Record<string, any> },
  { rejectValue: SubmitCancellationRejectValue }
>('cancellation/submit', async ({ orderId, payload }, { rejectWithValue }) => {
  const response = await submitCancellation(orderId, payload)
  const error = response?.data?.error
  if (error) {
    const reason = error?.ineligibility_reason
    const variant =
      reason === 'CANCELLATION_REASON_NOT_ELIGIBLE' ? 'reasonNotEligible' : reason ? 'tripNotEligible' : 'failed'
    return rejectWithValue({
      variant,
      labels: reason ? { title: reason, description: error?.message } : undefined,
    })
  }
  return response
})

export const fetchUpiIdStatus = createAsyncThunk(
  'cancellation/upiIdStatus',
  async ({ orderId, upiId }: { orderId: string; upiId: string }) => {
    const response = await verifyUpiId(orderId, upiId)
    return response
  },
)

export const fetchCancellationConfirmDetails = createAsyncThunk<
  CancellationRefundSnapshot,
  { tripId: string; cancellationId: string },
  { rejectValue: string }
>('cancellation/fetchConfirmDetails', async ({ tripId, cancellationId }) => {
  const response = await fetchCancellationOrderDetails(tripId, cancellationId)
  return response
})

export const cancellationSlice = createSlice({
  name: 'cancellation',
  initialState,
  reducers: {
    setCancellationSelection(
      state,
      action: PayloadAction<{
        selectedPaxIds: Record<string, string[]>
        reasonCode: string
        quotePayload: Record<string, unknown>
        cancellationId?: string
      }>,
    ) {
      state.selectedPaxIds = action.payload.selectedPaxIds
      state.reasonCode = action.payload.reasonCode
      state.quotePayload = action.payload.quotePayload
      state.cancellationId = action.payload.cancellationId || ''
    },
    setSelectedRefundMode: (state, action: PayloadAction<string>) => {
      state.uiStates.selectedRefundMode = action.payload
    },
    setUpiId: (state, action: PayloadAction<string>) => {
      state.uiStates.upiId = action.payload
    },
    resetCancellationOrderDetails(state) {
      state.orderDetails = null
      state.isOrderDetailsLoading = false
      state.orderDetailsError = null
    },
    setCancellationOrderDetails(state, action: PayloadAction<CancellationRefundSnapshot>) {
      state.orderDetails = action.payload
      state.isOrderDetailsLoading = false
      state.orderDetailsError = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCancellationReviewData.pending, state => {
        state.apiFetchStatus.isReviewLoading = true
      })
      .addCase(fetchCancellationReviewData.fulfilled, (state, action) => {
        state.apiFetchStatus.isReviewLoading = false
        if (isEmpty(action?.payload?.groups)) {
          state.apiFetchStatus.isReviewFailed = true
        }
        state.review = action.payload || {}
      })
      .addCase(fetchCancellationReviewData.rejected, state => {
        state.apiFetchStatus.isReviewLoading = false
        state.apiFetchStatus.isReviewFailed = true
        state.review = {}
      })
      .addCase(fetchCancellationViewData.pending, state => {
        state.apiFetchStatus.isViewLoading = true
      })
      .addCase(
        fetchCancellationViewData.fulfilled,
        (state, action: PayloadAction<CancellationViewResponse>) => {
          state.apiFetchStatus.isViewLoading = false
          if (isEmpty(action?.payload?.groups)) {
            state.apiFetchStatus.isViewFailed = true
          }
          state.view = action.payload || {}
        },
      )
      .addCase(fetchCancellationViewData.rejected, state => {
        state.apiFetchStatus.isViewLoading = false
        state.view = {}
        state.apiFetchStatus.isViewFailed = true
      })
      .addCase(submitCancellationRequest.pending, state => {
        state.isSubmitting = true
        state.cancellationErrorVariant = null
        state.cancellationErrorLabels = null
      })
      .addCase(submitCancellationRequest.fulfilled, (state, action) => {
        state.isSubmitting = false
        state.cancellationId = action.payload?.data?.cancellation_id ?? ''
      })
      .addCase(submitCancellationRequest.rejected, (state, action) => {
        state.isSubmitting = false
        state.cancellationErrorVariant = action.payload?.variant ?? 'failed'
        state.cancellationErrorLabels = action.payload?.labels ?? null
      })
      .addCase(fetchUpiIdStatus.pending, state => {
        state.isUpiIdStatusLoading = true
      })
      .addCase(fetchUpiIdStatus.fulfilled, (state, action: PayloadAction<any>) => {
        state.isUpiIdStatusLoading = false
        const data = action.payload
        if (!isEmpty(data)) {
          const { valid, accountHolderName } = data
          state.upiIdStatus = {
            valid: !!valid,
            accountHolderName: valid ? accountHolderName : '',
          }
        }
      })
      .addCase(fetchUpiIdStatus.rejected, state => {
        state.isUpiIdStatusLoading = false
        state.upiIdStatus = {}
      })
      .addCase(fetchCancellationConfirmDetails.pending, state => {
        state.apiFetchStatus.isCancelConfirmLoading = true
        state.apiFetchStatus.isCancelConfirmFailed = false
      })
      .addCase(fetchCancellationConfirmDetails.fulfilled, (state, action) => {
        state.apiFetchStatus.isCancelConfirmLoading = false
        state.orderDetails = action.payload
        state.apiFetchStatus.isCancelConfirmFailed = false
      })
      .addCase(fetchCancellationConfirmDetails.rejected, (state, action) => {
        state.apiFetchStatus.isCancelConfirmLoading = false
        state.apiFetchStatus.isCancelConfirmFailed = true
        state.orderDetails = null
      })
  },
})

export const {
  setCancellationSelection,
  setSelectedRefundMode,
  setUpiId,
  resetCancellationOrderDetails,
  setCancellationOrderDetails,
} = cancellationSlice.actions

export const cancellationActions = cancellationSlice.actions

export const getCancellationData = (state: RootState) => state.cancellation ?? initialState

export const getCancellationUiStates = (state: RootState) =>
  state.cancellation?.uiStates ?? initialState.uiStates

export default cancellationSlice.reducer
